import React, { useState, useEffect, useRef } from 'react';
import { Link, useStaticQuery, graphql } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { Icon } from '@components/icons';
import { usePrefersReducedMotion } from '@hooks';

const StyledPostsSection = styled.section`
  max-width: 1000px;

  .inner {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }

  .section-header {
    display: flex;
    align-items: center;

    @media (max-width: 768px) {
      margin-bottom: 30px;
    }

    h2 {
      margin: 0;
    }

    a {
      ${({ theme }) => theme.mixins.inlineLink};
      margin-left: 30px;
      font-family: var(--font-mono);
      font-size: var(--fz-sm);
      color: var(--green);
      white-space: nowrap;
    }
  }

  .posts-grid {
    ${({ theme }) => theme.mixins.resetList};
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    grid-gap: clamp(15px, 3vw, 25px);
    position: relative;
    max-width: 100%;

    @media (min-width: 1200px) {
      grid-template-columns: repeat(3, minmax(300px, 1fr));
      max-width: 1200px;
    }

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      grid-gap: 15px;
    }

    @media (max-width: 480px) {
      grid-gap: 12px;
      grid-template-columns: minmax(280px, 1fr);
    }
  }

  .posts-buttons {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 20px;
    margin: 80px auto 0;

    @media (max-width: 768px) {
      flex-direction: column;
      gap: 15px;
    }
  }

  .more-button,
  .show-all-button {
    ${({ theme }) => theme.mixins.button};
  }
`;

const StyledPost = styled.li`
  position: relative;
  cursor: default;
  transition: var(--smooth-transition);
  height: 100%;
  min-height: 400px;
  display: flex;

  @media (prefers-reduced-motion: no-preference) {
    &:hover,
    &:focus-within {
      .post-inner {
        transform: translateY(-7px);
        box-shadow: 0 25px 50px -15px var(--navy-shadow), 0 0 0 1px rgba(100, 255, 218, 0.1);
      }

      .post-image img,
      .post-image .gatsby-image-wrapper {
        transform: scale(1.02);
      }
    }
  }

  a {
    position: relative;
    z-index: 1;
  }

  .post-inner {
    ${({ theme }) => theme.mixins.boxShadow};
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    position: relative;
    height: 100%;
    width: 100%;
    background-color: var(--light-navy);
    border-radius: var(--border-radius);
    transition: var(--smooth-transition);
    overflow: hidden;

    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 2px;
      background: linear-gradient(90deg, var(--green), var(--blue));
      transform: translateX(-100%);
      transition: transform 0.4s cubic-bezier(0.23, 1, 0.32, 1);
    }

    &:hover:before {
      transform: translateX(0);
    }
  }

  .post-image {
    position: relative;
    width: 100%;
    height: 180px;
    overflow: hidden;
    border-radius: var(--border-radius) var(--border-radius) 0 0;
    background-color: var(--lightest-navy);
    background-image: linear-gradient(135deg, var(--navy) 0%, var(--lightest-navy) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    img,
    .gatsby-image-wrapper {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
      transform-origin: center center;
    }

    /* Ensure Gatsby images don't overflow */
    .gatsby-image-wrapper > div {
      padding-bottom: 0 !important;
    }

    .gatsby-image-wrapper img {
      position: absolute !important;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .placeholder-icon {
      color: var(--light-slate);
      font-size: 3rem;
      opacity: 0.3;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
      text-align: center;
      z-index: 2;
      position: relative;

      .title {
        font-size: var(--fz-sm);
        font-family: var(--font-mono);
        text-transform: uppercase;
        letter-spacing: 0.1em;
      }
    }

    &:after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(180deg, transparent 0%, rgba(10, 25, 47, 0.8) 100%);
      z-index: 1;
    }
  }

  .post-content {
    padding: 2rem 1.75rem;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  }

  .post-header {
    margin-bottom: 20px;
  }

  .post-category {
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
    font-weight: 400;
    line-height: 1.5;
    margin-bottom: 10px;
    text-transform: uppercase;
    letter-spacing: 0.05em;

    .category-badge {
      display: inline-block;
      padding: 4px 8px;
      border-radius: 3px;
      border: 1px solid;

      &.workshop {
        color: var(--blue);
        border-color: var(--blue);
        background-color: rgba(87, 203, 255, 0.1);
      }

      &.blog {
        color: var(--white-new);
        border-color: var(--white-new);
        background-color: var(--white-new-tint);
      }

      &.photography {
        color: var(--pink);
        border-color: var(--pink);
        background-color: rgba(245, 125, 255, 0.1);
      }

      &.research {
        color: var(--light-slate);
        border-color: var(--light-slate);
        background-color: rgba(168, 178, 209, 0.1);
      }

      &.default {
        color: var(--lightest-slate);
        border-color: var(--lightest-slate);
        background-color: rgba(204, 214, 246, 0.1);
      }
    }
  }

  .post-title {
    margin: 0 0 10px;
    color: var(--lightest-slate);
    font-size: var(--fz-xl);
    line-height: 1.3;

    a {
      position: static;

      &:before {
        content: '';
        display: block;
        position: absolute;
        z-index: 0;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
      }
    }
  }

  .post-description {
    color: var(--light-slate);
    font-size: var(--fz-sm);
    line-height: 1.5;
    margin-bottom: 20px;
    flex-grow: 1;

    a {
      ${({ theme }) => theme.mixins.inlineLink};
    }
  }

  .post-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: auto;
  }

  .post-date {
    font-family: var(--font-mono);
    font-size: var(--fz-xxs);
    color: var(--light-slate);
  }

  .post-links {
    display: flex;
    align-items: center;
    margin-right: -10px;
    color: var(--light-slate);

    a {
      ${({ theme }) => theme.mixins.flexCenter};
      padding: 5px 7px;

      &.external {
        svg {
          width: 18px;
          height: 18px;
          margin-top: -2px;
        }
      }

      svg {
        width: 16px;
        height: 16px;
      }
    }
  }
`;

const Posts = () => {
  const data = useStaticQuery(graphql`
    query {
      posts: allMarkdownRemark(
        filter: {
          fileAbsolutePath: { regex: "/content/posts/" }
          frontmatter: { draft: { ne: true } }
        }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              description
              date(formatString: "MMM DD, YYYY")
              category
              tags
              featured
              external
              github
              linkedin
              cover {
                childImageSharp {
                  gatsbyImageData(
                    width: 400
                    height: 180
                    placeholder: BLURRED
                    formats: [AUTO, WEBP, AVIF]
                    transformOptions: { cropFocus: CENTER, fit: COVER }
                  )
                }
              }
            }
            fields {
              slug
            }
            excerpt
            html
          }
        }
      }
    }
  `);

  const [showMore, setShowMore] = useState(false);
  const revealTitle = useRef(null);
  const revealPosts = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealTitle.current, srConfig());
    revealPosts.current.forEach((ref, i) => sr.reveal(ref, srConfig(i * 100)));
  }, []);

  const INITIAL_LIMIT = 3;
  const posts = data.posts.edges.filter(({ node }) => node);
  const firstThree = posts.slice(0, INITIAL_LIMIT);
  const postsToShow = showMore ? posts : firstThree;

  const getCategoryClass = category => {
    if (!category) {
      return 'default';
    }
    return category.toLowerCase();
  };

  const postInner = node => {
    const { frontmatter, fields, excerpt } = node;
    const { title, description, date, category, external, github, linkedin, cover } = frontmatter;
    const { slug } = fields;

    const coverImage = getImage(cover);
    const postDescription = description || excerpt;
    const postUrl = external || slug;
    const isExternal = !!external;

    return (
      <div className="post-inner">
        <div className="post-image">
          {coverImage ? (
            <GatsbyImage image={coverImage} alt={title} />
          ) : (
            <div className="placeholder-icon">
              <div>�</div>
              <div className="title">No Cover</div>
            </div>
          )}
        </div>

        <div className="post-content">
          <header className="post-header">
            {category && (
              <div className="post-category">
                <span className={`category-badge ${getCategoryClass(category)}`}>{category}</span>
              </div>
            )}

            <h3 className="post-title">
              {isExternal ? (
                <a href={postUrl} target="_blank" rel="noreferrer">
                  {title}
                </a>
              ) : (
                <Link to={postUrl}>{title}</Link>
              )}
            </h3>

            {postDescription && (
              <div
                className="post-description"
                dangerouslySetInnerHTML={{ __html: postDescription }}
              />
            )}
          </header>

          <footer className="post-footer">
            <span className="post-date">{date}</span>

            <div className="post-links">
              {github && (
                <a href={github} aria-label="GitHub Link" target="_blank" rel="noreferrer">
                  <Icon name="GitHub" />
                </a>
              )}
              {linkedin && (
                <a href={linkedin} aria-label="LinkedIn Link" target="_blank" rel="noreferrer">
                  <Icon name="LinkedIn" />
                </a>
              )}
              {external && (
                <a
                  href={external}
                  aria-label="External Link"
                  className="external"
                  target="_blank"
                  rel="noreferrer">
                  <Icon name="External" />
                </a>
              )}
              {!external && (
                <Link to={slug} aria-label="Read More">
                  <Icon name="External" />
                </Link>
              )}
            </div>
          </footer>
        </div>
      </div>
    );
  };

  return (
    <StyledPostsSection id="posts">
      <div className="inner">
        <div className="section-header">
          <h2 className="numbered-heading" ref={revealTitle}>
            Posts
          </h2>
          <Link to="/posts">view all posts</Link>
        </div>
      </div>

      <ul className="posts-grid">
        {postsToShow &&
          postsToShow.map(({ node }, i) => (
            <StyledPost key={i} ref={el => (revealPosts.current[i] = el)}>
              {postInner(node)}
            </StyledPost>
          ))}
      </ul>

      {posts.length > INITIAL_LIMIT && (
        <div className="posts-buttons">
          <button className="more-button" onClick={() => setShowMore(!showMore)}>
            Show {showMore ? 'Less' : 'More'}
          </button>
          <Link to="/posts" className="show-all-button">
            Show All Posts
          </Link>
        </div>
      )}
    </StyledPostsSection>
  );
};

export default Posts;
